import dayjs from "dayjs";
import "dayjs/locale/en-in";
import localizedFormat from "dayjs/plugin/localizedFormat";
import {
  Calendar,
  CheckCircle,
  Clock,
  PlayCircle,
  Plus,
  Star,
} from "lucide-react";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { ExpandableTile } from "../pages/infoTile";

dayjs.extend(localizedFormat);
dayjs.locale("en-in");

// Call to Action Button Component
const CallToActionButton = ({
  className = "",
  handleClick,
  ctaText,
}: {
  className?: string;
  handleClick: any;
  ctaText?: any;
}) => (
  <button
    className={`max-sm:hidden bg-gradient-to-r from-green-800 via-green-500 to-green-600 !text-white font-bold text-lg md:text-xl px-6 md:px-8 py-4 md:py-5 rounded-full flex items-center gap-3 ${className}`}
    onClick={handleClick}
  >
    <span>
      {ctaText ?? "Explore an Advanced AI-Powered Investing Platform"}
    </span>
  </button>
);

// Feature Card Component
const FeatureCard = ({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) => (
  <div className="bg-gradient-to-b from-blue-600 to-purple-900 p-6 md:p-8 rounded-xl  shadow-lg">
    <div className="flex items-start gap-4">
      <div className="flex items-center justify-center !w-16 h-auto md:!w-20 md:h-auto  aspect-square bg-gradient-to-br from-blue-400 to-purple-600 rounded-lg">
        {icon}
      </div>
      <div className="flex-1">
        <h3 className="text-lg md:text-xl font-bold text-white mb-3">
          {title}
        </h3>
        <div className="text-white text-sm md:text-base leading-relaxed">
          {description}
        </div>
      </div>
    </div>
  </div>
);

// Bonus Card Component
const BonusCard = ({
  number,
  title,
  description,
  icon,
}: {
  number: string;
  title: string;
  description: string;
  icon: string;
}) => (
  <div className="relative bg-gradient-to-b from-white to-gray-100 rounded-3xl border-2 border-white p-8 shadow-lg">
    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-black to-yellow-600 text-white px-6 py-3 rounded-full text-lg font-bold">
      BONUS {number}
    </div>
    <div className="flex flex-col items-center text-center mt-8">
      <img src={icon} alt="" className="w-20 h-20 mb-6" />
      <h3 className="text-2xl font-bold text-black mb-4 leading-tight">
        {title}
      </h3>
      <div className="text-black text-lg mb-6 leading-relaxed">
        {description}
      </div>
    </div>
  </div>
);

// Mentor Card Component
const MentorCard = ({
  name,
  image,
  achievements,
}: {
  name: string;
  image: string;
  achievements: string[];
}) => (
  <div className="grid md:grid-cols-2 gap-8 items-center">
    <img
      src={image}
      alt={name}
      className="w-full h-auto object-cover rounded-2xl"
    />
    <div>
      <h3 className="text-3xl md:text-4xl font-bold text-yellow-400 mb-8 text-center">
        {name}
      </h3>
      <div className="space-y-4">
        {achievements.map((achievement, index) => (
          <div key={index} className="flex items-start gap-3">
            <CheckCircle className="w-4 h-4 text-yellow-400 mt-1 flex-shrink-0" />
            <div className="text-white text-lg leading-relaxed">
              {achievement}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// FAQ Item Component
const FAQItem = ({
  question,
  isOpen,
  onClick,
}: {
  question: string;
  isOpen: boolean;
  onClick: () => void;
}) => (
  <div className="border-b border-gray-700">
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between py-6 text-left !text-black  "
    >
      <span className="text-lg font-medium">{question}</span>
      <Plus
        className={`w-5 h-5 transform transition-transform ${
          isOpen ? "rotate-45" : ""
        }`}
      />
    </button>
  </div>
);

// Main Landing Page Component
export function BuilderComp1({
  setLeadFormOpen,
  activeSlot,
  ctaText,
  formCtaText,
  time,
  fixedCtaText
}: {
  setLeadFormOpen: (args: any) => void;
  activeSlot: any;
  ctaText: string;
  formCtaText: string;
  time?:string;
  fixedCtaText?:string;
}) {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Features data
  const features = [
    {
      icon: (
        <div className="w-full h-full bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center text-[44px]">
          📊
        </div>
      ),
      title:
        "Use AI to identify high-growth sectors & conduct Professional  Analysis in 2 Minutes",
      description: "",
    },
    {
      icon: (
        <div className="w-full h-full bg-gradient-to-br from-green-400 to-blue-500 rounded-lg flex items-center justify-center text-[44px]">
          📈
        </div>
      ),
      title:
        "Make  more Accurate & Faster Investing Decisions and Outperform The Market",
      description: "",
    },
    {
      icon: (
        <div className="w-full h-full bg-gradient-to-br from-purple-400 to-pink-500 rounded-lg flex items-center justify-center text-[44px]">
          🎯
        </div>
      ),
      title:
        "Use AI to Predict the Markets  without even having any Pre-Requisite Knowledge ",
      description: "",
    },
    {
      icon: (
        <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg flex items-center justify-center text-[44px]">
          💼
        </div>
      ),
      title:
        "Achieve Portfolio Growth and Secure Your Place Among the Top 1% Investors",
      description: "",
    },
    {
      icon: (
        <div className="w-full h-full bg-gradient-to-br from-indigo-400 to-purple-500 rounded-lg flex items-center justify-center text-[44px]">
          🔍
        </div>
      ),
      title:
        "Use AI to uncover hidden trends and patterns for smarter investing",
      description: "",
    },
    {
      icon: (
        <div className="w-full h-full bg-gradient-to-br from-pink-400 to-red-500 rounded-lg flex items-center justify-center text-[44px]">
          💎
        </div>
      ),
      title:
        "Use AI to Pin-Point the best Stocks with Deep Fundamental & Technical Research.",
      description: "",
    },
  ];

  // Bonuses data
  const bonuses = [
    {
      number: "1",
      title: "Free Ebooks Library",
      description:
        "Unlock expert insights and proven strategies with our exclusive collection of free trading and investing ebooks",
      icon: "https://api.builder.io/api/v1/image/assets/TEMP/b6f86e814a2a2ed39a4358506776d7773e1188bc?width=200",
    },
    {
      number: "2",
      title: "In-Depth AI Tools Analysis with Ready-to-Use Prompts",
      description:
        "Discover powerful AI tools for traders and investors, with pre-built prompts to supercharge your research and decision-making.",
      icon: "https://api.builder.io/api/v1/image/assets/TEMP/a0e66958ebea5140f64649e5b121dca65aef6a08?width=190",
    },
    {
      number: "3",
      title: "AI-Powered Stock Comparison & Deep Research Prompts",
      description:
        "Quickly compare stocks, analyze fundamentals, and uncover hidden opportunities using AI-driven research prompt",
      icon: "https://api.builder.io/api/v1/image/assets/TEMP/5d5d7ebf2f0672e823e4ef4ecfa1510f8c99900b?width=190",
    },
    {
      number: "4",
      title: "AI Chart Analyzer Bot for Smarter Trading Decisions",
      description:
        "Analyze charts, identify trends, and make confident buy/sell decisions with our AI-powered chart analysis bot.",
      icon: "https://api.builder.io/api/v1/image/assets/TEMP/e71fccc16044a88b35bc399f8daf0344b526eb9b?width=190",
    },
  ];

  // FAQ data
  const router = useRouter();
  const comment = router.query.comment;
  return (
    <div
      className="min-h-screen bg-black text-white"
      style={{ fontFamily: "Montserrat, sans-serif" }}
    >
      {/* Font import moved to _document.tsx to avoid hydration issues */}

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-black via-blue-900 to-blue-950 min-h-screen flex items-center">
        <div className="container mx-auto px-4 max-sm:py-3 sm:py-20 max-w-7xl">
          <div className="text-center sm:mb-12">
            <HeaderSelector comment={comment as string} />
            <div className="text-lg md:text-2xl mb-4">
              <span className="text-white">Limited Seats Available</span>-
              <span className="text-yellow-400 font-bold">
                {" "}
                Live Session on Smart Investing Approaches
              </span>
            </div>
            <div className="text-gray-400 italic my-2 text-xs">
              5000+ investors already enrolled!
            </div>
            <h1 className="text-2xl md:text-5xl lg:text-6xl font-bold leading-tight sm:mb-6">
              <span className="text-white">MASTER </span>
              <span className="text-green-500">
                AI- BASED
                <br /> INVESTING{" "}
              </span>
              <span className="text-white">WITH </span>
              <span className="text-green-500">AKSHAY GULATI</span>
            </h1>

            {/* <div className="text-xl md:text-2xl text-yellow-400 font-bold mb-12">
              No Need To Trade or Invest Manually Ever Again!
            </div> */}
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center ">
            {/* Video Section */}
            <div className="relative max-sm:grid max-sm:grid-cols-1 max-sm:gap-4 ">
              <img
                src="/akshay-ai-investing/hero.webp"
                alt="Workshop Preview"
                className="w-full rounded-3xl shadow-2xl max-sm:order-2"
              />

              {/* Workshop Details Overlay */}
              <div className="mt-6 grid grid-cols-2 gap-4 max-sm:order-1">
                <div className="bg-white rounded-xl p-4 flex items-center gap-3">
                  <Calendar className="text-blue-600" />
                  <div>
                    <div className="font-bold text-black text-sm">
                      {isClient && activeSlot?.startDateTime
                        ? dayjs(activeSlot.startDateTime).format("DD MMMM YYYY")
                        : "Loading..."}
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 flex items-center gap-3">
                  <Clock className="text-blue-600" />
                  <div>
                    <div className="font-bold text-black text-sm">
                      {time?time:isClient && activeSlot?.startDateTime
                        ? dayjs(activeSlot.startDateTime).format("hh:mm A")
                        : "Loading..."}
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 flex items-center gap-3">
                  <Clock className="text-blue-600" />
                  <div>
                    <div className="font-bold text-black text-sm">2+ hours</div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 flex items-center gap-3">
                  <PlayCircle className="text-blue-600" />
                  <div>
                    <div className="font-bold text-black text-sm">
                      Live Session
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center lg:text-left">
              <h2 className="text-2xl md:text-3xl font-bold mb-8">
                <span className="text-white">Learn To </span>
                <span className="text-green-500">Grow Your Wealth</span>
                <span className="text-white"> With </span>
                <span className="text-yellow-400">AI Automation?</span>
              </h2>

              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-1">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <div className="text-lg font-bold text-white text-left">
                    AI will Do Hours of Research and presents insights faster
                    and You dont need any Financial Background to Invest
                  </div>
                </div>
                <div className="flex items-start gap-1">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <div className="text-lg font-bold text-white text-left">
                    AI provides data-driven insights and signals to support
                    better stock selection decisions.
                  </div>
                </div>
                <div className="flex items-start gap-1">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <div className="text-lg font-bold text-white text-left">
                    AI-assisted tools that will Create Portfolios as Per Your
                    Risk Appetite and Expectations
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center lg:items-start gap-6">
                <CallToActionButton
                  className="w-full lg:w-auto"
                  handleClick={() => setLeadFormOpen(true)}
                  ctaText={ctaText}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Tools Carousel Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 text-center max-w-7xl">
          <h2 className="text-3xl md:text-4xl font-medium text-white mb-12 leading-tight">
            The Powerful AI tools that You will Learn to transform
            <br />
            Trading & Investing Forever..!
          </h2>

          <div className="flex items-center justify-center gap-8 md:gap-20 flex-wrap">
            <img
              src="https://api.builder.io/api/v1/image/assets/TEMP/dc080300b4be8223fcc34aae04ef101022319e86?width=260"
              alt="ChatGPT"
              className="h-8 md:h-12 opacity-80 hover:opacity-100 transition-opacity"
            />
            <img
              src="https://api.builder.io/api/v1/image/assets/TEMP/10103bf45c27e372cb6e93643cee6e3f771b6b96?width=260"
              alt="Claude"
              className="h-8 md:h-12 opacity-80 hover:opacity-100 transition-opacity"
            />
            <img
              src="https://api.builder.io/api/v1/image/assets/TEMP/5633612f586d76d5385b304d922fc046b299405e?width=260"
              alt="Gemini"
              className="h-8 md:h-12 opacity-80 hover:opacity-100 transition-opacity"
            />
            <img
              src="https://api.builder.io/api/v1/image/assets/TEMP/58a7e406e096ec92b4646b71c3db50a4fcd56b7d?width=260"
              alt="Perplexity"
              className="h-8 md:h-12 opacity-80 hover:opacity-100 transition-opacity"
            />
            <img
              src="https://api.builder.io/api/v1/image/assets/TEMP/c4dcc6261171b046e6b76a77249b2bc486fde52c?width=260"
              alt="Copilot"
              className="h-8 md:h-12 opacity-80 hover:opacity-100 transition-opacity"
            />
          </div>
        </div>
      </section>

      {/* Why Attend Workshop Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-3xl md:text-4xl font-medium text-white text-center mb-16">
            WHY ATTEND THIS WORKSHOP?
          </h2>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <FeatureCard key={index} {...feature} />
            ))}
          </div>

          <div className="text-center mt-16">
            <CallToActionButton
              className="mx-auto"
              handleClick={() => setLeadFormOpen(true)}
              ctaText={ctaText}
            />
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 text-center max-w-7xl">
          <div className="flex justify-center mb-6">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-8 h-8 text-yellow-400 fill-current" />
            ))}
          </div>

          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 underline">
            <span className="text-yellow-400">5,000</span> PAST ATTENDEES...
          </h2>

          <div className="text-xl text-gray-400 mb-16">
            Recommend You To Attend This Workshop, If You Fit Any Of The
            Following ⬇️
          </div>

          <div className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto">
            <div className="text-center">
              <img
                src="https://api.builder.io/api/v1/image/assets/TEMP/4303938c326f78072241b9d1cda888556aacd663?width=200"
                alt="Beginner"
                className="w-24 h-36 mx-auto mb-6"
              />
              <h3 className="text-xl font-bold mb-4">
                <span className="text-white">You're A </span>
                <span className="text-yellow-400">Beginner</span>
                <span className="text-white">
                  {" "}
                  Wanting To Kickstart Your Financial Journey & Build Wealth
                </span>
              </h3>
              <div className="text-gray-300 leading-relaxed">
                From students to entrepreneurs, discover how AI can empower you
                to build wealth and secure financial freedom.
              </div>
            </div>

            <div className="text-center">
              <img
                src="https://api.builder.io/api/v1/image/assets/TEMP/60cf5d5881bc5b9d6c2247d2fefe1e1d5011ad73?width=360"
                alt="Trader"
                className="w-32 h-28 mx-auto mb-6"
              />
              <h3 className="text-xl font-bold mb-4">
                <span className="text-white">You're An </span>
                <span className="text-yellow-400">Investor</span>
                <span className="text-white">
                  {" "}
                  Wanting To Skyrocket Your Success
                </span>
              </h3>
              <div className="text-gray-300 leading-relaxed">
                Revolutionise your strategies with AI-driven market insights
              </div>
            </div>

            <div className="text-center">
              <img
                src="https://api.builder.io/api/v1/image/assets/TEMP/e6562312d11e2bc6f3bd69ba3ad68b7c977cbeff?width=320"
                alt="Experienced Trader"
                className="w-28 h-26 mx-auto mb-6"
              />
              <h3 className="text-xl font-bold mb-4">
                <span className="text-white">You're An </span>
                <span className="text-yellow-400">Experienced Investor</span>
                <span className="text-white">
                  {" "}
                  Ready To Master The Market & Stay Ahead Of The Curve
                </span>
              </h3>
              <div className="text-gray-300 leading-relaxed">
                Seasoned professionals and Avid Investors, harness the full
                potential of AI to scale your strategies and discover further
                edge to dominate the market.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Benefits Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-3xl md:text-5xl font-medium text-white text-center mb-16 leading-tight">
            Here's How AI Will Change Investing &<br />
            Trading Forever
          </h2>

          <div className="max-w-4xl mx-auto space-y-8">
            <div className="blue-gradient-box p-8 md:p-12 rounded-3xl">
              <div className="border-l-8 border-yellow-400 pl-8">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                  <span className="text-white">Mantra To </span>
                  <span className="text-white">Be More Profitable</span>
                </h3>
                <div className="text-lg text-white leading-relaxed">
                  Yield the power of AI with Multiple AI Tools to consistently
                  discover unique opportunities in the market and leverage it to
                  generate profits while others falter. Be Smarter, Faster &
                  More Profitable than 99% Others
                </div>
              </div>
            </div>

            <div className="blue-gradient-box p-8 md:p-12 rounded-3xl">
              <div className="border-l-8 border-yellow-400 pl-8">
                <h3 className="text-2xl md:text-3xl font-bold mb-6">
                  <span className="text-white">
                    {" "}
                    Witness AI-Powered Investing in{" "}
                    <span className="text-yellow-400">Live</span> Action!
                  </span>
                </h3>
                <div className="text-lg text-white leading-relaxed">
                  Join our LIVE session to explore market data, identify trading opportunities, and learn how AI-driven automated trading works.
                </div>
              </div>
            </div>

            <div className="blue-gradient-box p-8 md:p-12 rounded-3xl">
              <div className="border-l-8 border-yellow-400 pl-8">
                <h3 className="text-2xl md:text-3xl font-bold mb-6">
                  <span className="text-yellow-400">2 Minute</span>
                  <span className="text-white"> AI-Powered Analysis</span>
                </h3>
                <div className="text-lg text-white leading-relaxed">
                  Unlock Exclusive Ground Breaking AI Powered Analysis Tools.
                  From identifying best sectors, to comparing companies, to
                  accurately picking stocks! Months of professional Grade
                  Analysis in 2 Mins!
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-16">
            <CallToActionButton
              className="mx-auto"
              handleClick={() => setLeadFormOpen(true)}
              ctaText={ctaText}
            />
          </div>
        </div>
      </section>

      {/* Bonuses Section */}
      <section className="py-20 bg-black relative">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-16">
            <div className="inline-block bg-yellow-300 text-black px-8 py-2 rounded-lg mb-6">
              <div className="text-xl font-medium">
                WAIT! There's More Awesomeness In Store For You
              </div>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold leading-tight">
              <span className="text-white">
                Immediate Access to Actionable BONUSES Worth{" "}
              </span>
              <span className="text-yellow-400 line-through">Rs.18,924/-</span>
              <br />
              <span className="text-white">Absolutely </span>
              <span className="text-green-500">FREE</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto mb-16">
            {bonuses.map((bonus) => (
              <BonusCard key={bonus.number} {...bonus} />
            ))}
          </div>

          <div className="text-center">
            <div className="text-white text-xl font-bold mb-6">
              Unlock <span className="font-bold">FREE BONUSES</span> Worth{" "}
              <span className="font-bold">Rs. 18,924</span> If You Act Before
              the Timer Hits ZERO!
            </div>

            <CallToActionButton
              className="mx-auto"
              handleClick={() => setLeadFormOpen(true)}
              ctaText={ctaText}
            />
          </div>
        </div>
      </section>

      {/* What You'll Learn Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-16">
            THIS WORKSHOP WILL EMPOWER YOU TO 👇
          </h2>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
            <div className="blue-gradient-box p-6 rounded-2xl relative overflow-hidden">
              <div className="text-center relative z-10">
                <div className="w-32 h-auto aspect-auto mx-auto">
                  <img
                    src="https://api.builder.io/api/v1/image/assets/TEMP/adc2c674dfce3e9f1d54ed0173c74cc076880415?width=312"
                    alt=""
                    className="w-full h-full "
                  />
                </div>
                <h3 className="text-lg font-bold text-white leading-tight">
                  Ideate & create Powerful Investing Frameworks Using Al in
                  minutes
                </h3>
              </div>
            </div>

            <div className="blue-gradient-box p-6 rounded-2xl relative overflow-hidden">
              <div className="text-center relative z-10">
                <div className="w-32 h-auto aspect-auto mx-auto  ">
                  <img
                    src="https://api.builder.io/api/v1/image/assets/TEMP/adc2c674dfce3e9f1d54ed0173c74cc076880415?width=312"
                    alt=""
                    className="w-full h-full"
                  />
                </div>
                <h3 className="text-lg font-bold text-white leading-tight">
                  Use Al to build a Winner Portfolio like Professional
                  Investment Managers Using Al
                </h3>
              </div>
            </div>

            <div className="blue-gradient-box p-6 rounded-2xl relative overflow-hidden">
              <div className="text-center relative z-10">
                <div className="w-32 h-auto aspect-auto mx-auto ">
                  <img
                    src="https://api.builder.io/api/v1/image/assets/TEMP/adc2c674dfce3e9f1d54ed0173c74cc076880415?width=312"
                    alt=""
                    className="w-full h-full"
                  />
                </div>
                <h3 className="text-lg font-bold text-white leading-tight">
                  Identify Superior Investing setups with Al before 99% others &
                  secure untapped opportunities
                </h3>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto ">
            <div className="blue-gradient-box p-6 rounded-2xl relative overflow-hidden">
              <div className="text-center relative z-10">
                <div className="w-32 h-auto aspect-auto mx-auto   ">
                  <img
                    src="https://api.builder.io/api/v1/image/assets/TEMP/715807ca6ad082cdc795cf6e62467933cbc7948e?width=472"
                    alt=""
                    className="w-full h-full"
                  />
                </div>
                <h3 className="text-lg font-bold text-white leading-tight">
                  Put Your Wealth on Fast track with Al. Be Smarter, faster, and
                  more accurate with Al.
                </h3>
              </div>
            </div>

            <div className="blue-gradient-box p-6 rounded-2xl relative overflow-hidden">
              <div className="text-center relative z-10">
                <div className="w-32 h-auto aspect-auto mx-auto   ">
                  <img
                    src="https://api.builder.io/api/v1/image/assets/TEMP/715807ca6ad082cdc795cf6e62467933cbc7948e?width=472"
                    alt=""
                    className="w-full h-full"
                  />
                </div>
                <h3 className="text-lg font-bold text-white leading-tight">
                  Do professional grade fundamental & technical analysis in just
                  2 minutes using powerful AI tools.
                </h3>
              </div>
            </div>
          </div>

          <div className="text-center space-y-8 mt-20">
            <h2 className="text-3xl md:text-5xl font-bold text-white">
              THE DECISION IS YOURS TO MAKE...
            </h2>

            <div className="text-xl font-bold leading-relaxed">
              <span className="text-white">Ready To Craft Your Own </span>
              <span className="text-green-500">
                100% AI-Driven Investing Journey
              </span>
              <span className="text-white">
                ?<br />
                Let AI work tirelessly To Build Your Wealth!
              </span>
            </div>

            <CallToActionButton
              className="mx-auto"
              handleClick={() => setLeadFormOpen(true)}
              ctaText={ctaText}
            />

            <div className="text-xl font-bold leading-relaxed">
              <span className="text-white">
                With AI, Success in Investing & Trading, is not just a{" "}
              </span>
              <span className="text-yellow-400">Dream</span>
              <span className="text-white">, but </span>
              <span className="text-yellow-400">YOUR</span>
              <span className="text-white">
                {" "}
                Gateway to Making it a Reality!
              </span>
            </div>

          </div>
        </div>
      </section>

      {/* Mentors Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-16">
            <span className="text-white">MEET YOUR </span>
            <span className="text-yellow-400">MENTOR</span>
          </h2>

          <div className="space-y-20">
            <MentorCard
              name="AKSHAY GULATI"
              image="/akshay-ai-investing/paidTeacher.webp"
              achievements={[
                "6+ years of investing experience",
                "IIT Guwahati Alumnus",
                "Avid Angel Investor",
                "Founder at The Future University",
                "Taught More than 50k Investors",
                "Created India's first AI based investing tool",
              ]}
            />
          </div>

          <div className="text-center mt-16">
            <CallToActionButton
              className="mx-auto"
              handleClick={() => setLeadFormOpen(true)}
              ctaText={ctaText}
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-8">
            FAQ's
          </h2>

          <div className="text-center mb-16">
            <div className="text-xl text-white leading-relaxed">
              We've tried our best to answer all common queries that you might
              have.
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            <Faq />
          </div>
          <div className="text-center mb-16">
            <CallToActionButton
              className="mx-auto"
              handleClick={() => setLeadFormOpen(true)}
              ctaText={ctaText}
            />
          </div>
        </div>
      </section>

      {/* Bottom Sticky Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-black shadow-2xl border-t border-gray-800 p-4 z-50">
        <div className="container mx-auto flex items-center justify-between max-w-7xl max-sm:justify-center">
          <button
            className="bg-gradient-to-r from-green-800 via-green-500 to-green-800 !text-white text-[16px] sm:text-lg font-bold px-6 py-3 rounded-full w-full "
            onClick={() => {
              setLeadFormOpen(true);
            }}
          >
            {fixedCtaText?fixedCtaText:"Explore an Advanced AI-Powered Investing Platform"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Faq() {
  return (
    <div className="w-full pb-24">
      <div className="w-full flex flex-col gap-5 items-center">
        {FAQ_Content.map((faq: any, ind) => (
          <ExpandableTile
            header={faq.question}
            key={ind}
            className="!text-black !bg-white"
          >
            {faq.answer}
          </ExpandableTile>
        ))}
      </div>
    </div>
  );
}
const FAQ_Content = [
  {
    question: "Is it a live or pre-recorded workshop?",
    answer: "It is a live workshop.",
  },
  {
    question: "Will recording be provided?",
    answer: "No, the recording will not be provided.",
  },
  {
    question: "How much capital is required?",
    answer:
      "While you can join the class to learn the process, implementing strategies may require a capital of Rs. 10,000–15,000.",
  },
  {
    question: "Will there be a QnA session in the class?",
    answer:
      "Yes, there will be a Q&A session after each step of the class to support your learning.",
  },
];

const HeaderSelector = ({ comment }: { comment?: string }) => {
  if (comment) {
    if (comment.includes("founder-gyaan")) {
      return <span> In Exclusive Partnership with Founder Gyaan </span>;
    }
  }
  return null;
};
